import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { useCartStore } from '../stores/cartStore'
import { api } from '../lib/api'
import ShippingForm from '../components/ShippingForm'
import OrderSummary from '../components/OrderSummary'
import PaymentMethodSelector from '../components/PaymentMethodSelector'
import Footer from '../components/Footer'
import type { ShippingInfo, PaymentMethodType } from '../types'

function CheckoutPage() {
  const navigate = useNavigate()
  const items = useCartStore((state) => state.items)
  const clearCart = useCartStore((state) => state.clearCart)
  const getCartTotals = useCartStore((state) => state.getCartTotals)

  // Payment method state
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethodType | undefined>()
  const [paymentFee, setPaymentFee] = useState<number>(0)
  const [grandTotal, setGrandTotal] = useState<number>(0)

  const totals = getCartTotals()

  const createOrderMutation = useMutation({
    mutationFn: (data: {
      items: Array<{ productId: string; quantity: number }>
      shippingInfo: ShippingInfo
      paymentMethodType?: PaymentMethodType
      paymentMethodFee?: number
      grandTotal?: number
    }) => api.createOrder(data),
    onSuccess: (order) => {
      clearCart()
      navigate(`/order-success/${order.id}`)
    },
  })

  const handlePaymentMethodSelect = (method: PaymentMethodType, totalWithFee: number) => {
    setSelectedPaymentMethod(method)
    setPaymentFee(totalWithFee - totals.total)
    setGrandTotal(totalWithFee)
  }

  const handleSubmit = (info: ShippingInfo) => {
    if (!selectedPaymentMethod) {
      alert('Silakan pilih metode pembayaran')
      return
    }

    const orderData = {
      items: items.map((item) => ({
        productId: item.product.id,
        quantity: item.quantity,
      })),
      shippingInfo: info,
      paymentMethodType: selectedPaymentMethod,
      paymentMethodFee: paymentFee,
      grandTotal,
    }

    createOrderMutation.mutate(orderData)
  }

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-4">Keranjang Kosong</h1>
            <p className="text-gray-600 mb-6">Tambahkan produk ke keranjang terlebih dahulu</p>
            <button onClick={() => navigate('/')} className="btn-primary">
              Kembali ke Toko
            </button>
          </div>
        </div>
        <Footer />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <div className="flex-1 py-8">
        <div className="container mx-auto px-4 max-w-6xl">
          <h1 className="text-3xl font-bold mb-8">Checkout</h1>

          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              {/* Shipping Form */}
              <ShippingForm
                onSubmit={handleSubmit}
                isLoading={createOrderMutation.isPending}
                error={createOrderMutation.error?.message}
              />

              {/* Payment Method Selection */}
              <PaymentMethodSelector
                amount={totals.total}
                onSelect={handlePaymentMethodSelect}
                selectedMethod={selectedPaymentMethod}
              />
            </div>

            <div className="lg:col-span-1">
              <OrderSummary
                items={items}
                paymentMethod={selectedPaymentMethod}
                paymentFee={paymentFee}
                grandTotal={grandTotal || totals.total}
              />
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  )
}

export default CheckoutPage
